import { Calendar, Day, DayOfWeek, Month, Week, Event, ReservationType } from './models/calendar.model';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { NEVER } from 'rxjs';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent implements OnInit, OnChanges {
  @Input() events: Event[] = [];
  @Input() locale;
  @Output() eventSelect = new EventEmitter<Event>();
  @Output() daySelect = new EventEmitter<Date>();

  calendar: Calendar;
  currentDate: Date;

  days: string[] = [];
  months: string[] = [];
  years: number[] = [];
  currentMonth: string;
  currentYear: number;

  showMonthOptions: boolean;
  showYearOptions: boolean;

  currentMonthIndex: number;
  ReservationType = ReservationType;


  constructor() { }

  ngOnInit() {
    if (!this.locale) {
      this.locale = navigator.language;
    }
    this.initDayAndMonths();
    this.initCalendar();
  }

  ngOnChanges() {
    this.initCalendar();
    this.initDayAndMonths();
  }

  initDayAndMonths() {
    this.years = [];
    this.months = [];
    this.days = [];
    const date = new Date();
    const currentMonthIndex = date.getMonth();
    this.currentYear = date.getFullYear();

    for (let i = this.currentYear - 10; i <= this.currentYear + 10; i++) {
      this.years.push(i);
    }

    date.setDate(1);
    for (let i = 0; i < 12; i++) {
      date.setMonth(i);
      this.months.push(date.toLocaleString(this.locale, { month: 'long' }));
    }
    this.currentMonth = this.months[currentMonthIndex];

    while (date.getDay() !== 1) {
      date.setDate(date.getDate() + 1);
    }
    for (let i = 0; i < 7; i++) {
      this.days.push(date.toLocaleString(this.locale, { weekday: 'short' }));
      date.setDate(date.getDate() + 1);
    }
  }

  initCalendar(): void {
    this.currentDate = new Date();
    this.currentDate.setHours(0);
    this.currentDate.setMinutes(0);
    this.currentDate.setSeconds(0);
    this.currentDate.setMilliseconds(0);
    this.calendar = new Calendar();
    this.events.sort((a, b) => new Date(a.from).getTime() < new Date(b.from).getTime() ? 1 : 0);
    const startDate = new Date(this.currentDate);
    startDate.setFullYear(this.currentDate.getFullYear() - 10, 0, 1);

    const endDate = new Date(this.currentDate);
    endDate.setFullYear(this.currentDate.getFullYear() + 10, 11, 31);

    let week = new Week();
    let month = new Month({ year: startDate.getFullYear(), dateMonth: startDate.getMonth(), weeks: [] });
    while (!this.compareDates(startDate, endDate)) {
      const day = new Day({
        month: this.calendar.months.length,
        date: new Date(startDate),
        dayNumber: startDate.getDate(),
        dayOfWeek: startDate.getDay(),
        events: []
      });
      if (this.events && this.events.length > 0) {
        this.events.forEach(e => {
          const nEvent = new Event(e);
          if (nEvent.from <= day.date && nEvent.to >= day.date) {
            if (this.compareDates(nEvent.from, day.date)) {
              let position = 0;
              if (day.events.length > 0 && (day.events[0].isEnd || day.events[1]?.isEnd)) {
                day.events.forEach(positionEvent => {
                  if (positionEvent.isEnd) {
                    position = positionEvent.calendarPosition;
                  }
                });
              } else if (day.events.length === 1 && !day.events[0].isEnd) {
                position = day.events[0].calendarPosition === 1 ? 0 : 1;
              }
              e.calendarPosition = position;
              nEvent.calendarPosition = e.calendarPosition;
              nEvent.isStart = true;
            }
            if (this.compareDates(nEvent.to, day.date)) {
              nEvent.isEnd = true;
            }
            day.events.push(nEvent);
          }
        });
      }

      week.days.push(day);
      if (day.dayOfWeek === DayOfWeek.Sunday) {
        month.weeks.push(week);
        week = new Week();
      }
      if (this.isLastDayOfMonth(startDate)) {
        this.calendar.months.push(month);
        month = new Month({ year: startDate.getFullYear(), dateMonth: startDate.getMonth(), weeks: [] });
      }
      startDate.setDate(startDate.getDate() + 1);
    }
    this.adjustMonths();
  }

  adjustMonths() {
    this.calendar.months.forEach((month, index) => {
      let weekCount = 0;
      if (index < this.calendar.months.length - 1) {
        while (month.weeks.length < 6) {
          const nextMonth = this.calendar.months[index + 1];
          const nextMonthWeek = nextMonth.weeks[weekCount];
          month.weeks.push(nextMonthWeek);
          weekCount++;
        }
      }
    });
    this.setMonthIndex(this.currentDate);
  }

  setMonthIndex(date: Date): void {
    this.calendar.months.forEach((month, i) => {
      let dates: Date[] = [];
      month.weeks.map(x => dates = dates.concat(x.days.map(y => y.date)));
      if (dates.some(x => this.compareDates(x, date))) {
        this.currentMonthIndex = i;
        return;
      }
    });
  }

  isLastDayOfMonth(date: Date): boolean {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay.getDate() === 1;
  }

  compareDates(a: Date, b: Date): boolean {
    return (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate());
  }

  showName(day: Day, event: Event): boolean {
    const daysInMili = 3 * 24 * 60 * 60 * 1000; 
    if ([DayOfWeek.Saturday, DayOfWeek.Sunday].some(x => x === day.dayOfWeek)) {
      return false;
    }
    if (day.dayOfWeek === DayOfWeek.Monday && (event.to.getTime() - day.date.getTime() >= daysInMili)) {
      return true;
    }
    if (event.isStart) {
      return true;
    }
  }

  previousMonth(): void {
    if (this.currentMonthIndex === 0) {
      return;
    }
    this.currentMonthIndex--;
    const monthIndex = this.months.indexOf(this.currentMonth);
    if (monthIndex === 0) {
      this.currentMonth = this.months[11];
      this.currentYear--;
    } else {
      this.currentMonth = this.months[monthIndex - 1];
    }
  }

  nextMonth(): void {
    if (this.currentMonthIndex === this.calendar.months.length - 1) {
      return;
    }
    this.currentMonthIndex++;
    const monthIndex = this.months.indexOf(this.currentMonth);
    if (monthIndex === 11) {
      this.currentMonth = this.months[0];
      this.currentYear++;
    } else {
      this.currentMonth = this.months[monthIndex + 1];
    }
  }

  onMonthSelect(m: string) {
    this.currentMonth = m;
    this.changeMonthYear();
  }

  onYearSelect(y: number) {
    this.currentYear = y;
    this.changeMonthYear();
  }

  changeMonthYear(): void {
    const date = new Date(this.currentYear, this.months.indexOf(this.currentMonth), 1);
    this.setMonthIndex(date);
  }

  getShortMonth(date: Date): string {
    return date.toLocaleString(this.locale, { month: 'short' });
  }

  dayClick(date: Date) {
    this.daySelect.emit(date);
  }

  eventClick($event: any, e: Event) {
    $event.stopPropagation();
    this.eventSelect.emit(e);
  }

}
